// when board is rendered, and it's the computers turn
// it calls to here
// which figures out where to play
// sets action
// and then calls gameBoard take action, and I think it handles everything from there...

import { matchService } from "../matches/matchService_v2.js";
import {
  CENTER_HEXS,
  COMPUTER_USERID,
  DIRECTIONS,
  SCOREBONACCI,
  P1_REMOVES,
  P2_REMOVES,
} from "../constants_v2.js";
import {
  coordInPieces,
  isCenter,
  getNeighborCoords,
  isOnGrid,
  isDouble,
} from "./utils-game_v2.js";

// need to add the center hexes to the array, but only if they are empty
const addCenterHexes = (pieces) => {
  const centerPieces = [];
  for (const centerHex of CENTER_HEXS) {
    if (
      coordInPieces({ coords: centerHex.coords, piecesArray: pieces }) === -1
    ) {
      // -1 means the center hex is not in the pieces array
      // which means its empty, so we add it
      const centerPiece2Add = {
        player: "Empty",
        direction: "Empty",
        coords: centerHex.coords,
      };
      centerPieces.push(centerPiece2Add);
    } // and any other value means it's in the array which means
    // there is a piece on. So we don't add it to avoid duplicating it.
  }
  return pieces.concat(centerPieces);
};

/**
 * Tells you what would happen if you add a piece on a given coordinate, and the the piece
 * hypothetically is facing the direction provided
 * @param {{ coords2Check: coords, direction2Check:string }} Object
 * @returns [{player: "playerTwo", direction: "Up", coords: {s, r, q}},...] i.e. an array of game pieces that would be moving.
 */
const calcRilMovePlanHypothetical = ({
  coords2Check,
  direction2Check,
  piecesArray,
}) => {
  const movePlan = [];
  piecesArray = [...piecesArray];
  const startHex = gameRilCoordState({ coords: coords2Check, piecesArray });
  //what if it doesn't have a piece?
  if (startHex.player === "Empty") {
    return movePlan; //nothing to move
  }
  movePlan.push(startHex);
  let keepLooping = false;
  let neighbor;
  let movePlanValid = true;
  let safetyCheck = 0;
  do {
    neighbor = getRilNeighbor({
      coords: coords2Check,
      direction: direction2Check,
      piecesArray,
    });
    switch (true) {
      case coordInPieces({
        coords: neighbor.coords,
        piecesArray: [...movePlan],
      }) >= 0:
        keepLooping = false;
        movePlanValid = false; //because movePath hits itself
        break;
      case neighbor.player != "Empty" && neighbor.coords.s != -99:
        movePlan.push(neighbor);
        coords2Check = neighbor.coords;
        direction2Check = neighbor.direction;
        keepLooping = true; //not at end of movePath yet
        break;
      case neighbor.player === "Empty" && neighbor.coords.s != -99:
        keepLooping = false;
        movePlanValid = true; // because we hit an empty spot that's on the board
        break;
      case neighbor.coords.s === -99:
        keepLooping = false;
        movePlanValid = false; // because we hit the edge of the board
        break;
    }
    safetyCheck++; // because complicated do loop logic make me nervous
    if (safetyCheck === 100) {
      console.log("Error: MovePlan hit max 100 moves");
      console.log("The movePlan is: " + JSON.stringify(movePlan));
    }
  } while (keepLooping === true && safetyCheck < 101);
  if (movePlanValid === true) {
    return movePlan;
  } else {
    return [];
  }
};

/**
 * Tells you the state of a hexagon
 * @param {coords} coords i.e. an object with all three game coordinate axes
 * @returns {piece} piece { player:, direction, coords: {s,q,r} } i.e. a piece object
 * Case 1: The coords don't have a piece, so player and direction will be returned as "Empty"
 * Case 2: There is a piece. "player" is "playerOne" or "playerTwo" and "direction" is the way its facing
 * and the coords returned are the same coords received in Case 1 and 2.
 * Case 3: The coords are off the board, so it returns this:
 * { player: "Empty", direction: "Empty", coords:{s: -99 ,q: -99 ,r: -99 } }
 */
const gameRilCoordState = ({ coords, piecesArray }) => {
  var piece = {
    player: "Empty",
    direction: "Empty",
    coords: coords,
  };
  const index = coordInPieces({ coords, piecesArray });
  if (index != -1) {
    piece.player = piecesArray[index].player;
    piece.direction = piecesArray[index].direction;
  }
  return piece;
};

/**
 * Tells you what would happen if you play on a given game coordinate
 * @param {coords} coords {s, r, q} i.e. an object with all three game coordinate axes
 * @returns [{player: "playerTwo", direction: "Up", coords: {s, r, q}},...] i.e. an array of game pieces that would be moving.
 */
const calculateRilMovePlanOfPiece = ({ coords, piecesArray }) => {
  const startHex = gameRilCoordState({ coords, piecesArray });
  return calcRilMovePlanHypothetical({
    coords2Check: coords,
    direction2Check: startHex.direction,
    piecesArray,
  });
};

/**
 * Tells you if a game coordinate could be played on with a push action
 * @param {coords} coords {s, r, q} i.e. an object with all three game coordinate axes
 * @returns {object} The return object is {available: true or false , movePlan: []}
 * available true means you can push there, false means you can't
 * movePlan is the array of pieces that will have to be moved out of the way
 */
const isRilPushAvailable = ({ coords, piecesArray }) => {
  const inCenter = isCenter(coords);
  const returnObj = {
    available: inCenter,
    movePlan: [],
  };
  const index = coordInPieces({ coords, piecesArray });
  // case 1: first move of the game has to be in the middle
  if (piecesArray.length === 0 && inCenter) {
    return returnObj;
  }

  if (index === -1) {
    // case 2: It's empty. So you can't play on an empty hexagon that's not in the center
    return returnObj;
  } else {
    // case3: It's not empty. So can the piece move out of the way?
    returnObj.movePlan = calculateRilMovePlanOfPiece({ coords, piecesArray });
    returnObj.available = returnObj.movePlan.length === 0 ? false : true;
    return returnObj;
  }
};

const moveRilPieces = ({ tempPieces, movePlan }) => {
  movePlan.reverse();
  for (let piece2move of [...movePlan]) {
    const neighborCoords = getNeighborCoords({
      direction: piece2move.direction,
      coords: piece2move.coords,
    });
    const pieceMoved = {
      player: piece2move.player,
      direction: piece2move.direction,
      coords: neighborCoords,
    };
    let index = 0;
    //update
    for (let tempPiece of tempPieces) {
      if (tempPiece.coords === pieceMoved.coords) {
        break;
      }
      index++;
    }
    tempPieces[index] = pieceMoved;
  } // end of move plan iteration

  return tempPieces;
};

const addComputerPiece = ({ piece, tempPieces }) => {
  const result = matchService.getCurrentMatch();
  const match = result.currentMatch;
  const ourPlayer =
    match.p1UserID === COMPUTER_USERID ? "playerOne" : "playerTwo";
  const computerPiece = {
    player: ourPlayer,
    direction: "up",
    coords: piece.coords,
  };
  //update
  let index = 0;
  for (let tempPiece of tempPieces) {
    if (tempPiece.coords === computerPiece.coords) {
      break;
    }
    index++;
  }
  tempPieces[index] = computerPiece;
  return tempPieces;
};

const calcScoreDelta = (tempPieces) => {
  let p1Score = 0;
  let p2Score = 0;
  for (const tempPiece of tempPieces) {
    switch (tempPiece.player) {
      case "playerOne":
        p1Score =
          p1Score + calcHexScore({ piece: tempPiece, piecesArray: tempPieces });
        break;
      case "playerTwo":
        p2Score =
          p2Score + calcHexScore({ piece: tempPiece, piecesArray: tempPieces });
        break;
    }
  }

  return { p2Score, p1Score };
};

/**
 * Tells you if a game coordinate could be played on with a rotate action
 * You can only rotate your own pieces, and you can only rotate them to point in an unblocked direction
 * @param {coords} coords {s, r, q} i.e. an object with all three game coordinate axes
 * @returns {{available: boolean, validDirections:[string]}}} The return object is {available: true or false}
 * available true means you can rotate there, and returns the valid directions
 * false means you can't rotate the piece
 */
const isRilRotateAvailable = ({ coords, piecesArray }) => {
  const turn = matchService.getTurn().player;
  const piece2Rotate = gameRilCoordState({ coords, piecesArray });
  const validDirections = [];
  for (const direction of DIRECTIONS) {
    const movePlan = calcRilMovePlanHypothetical({
      coords2Check: coords,
      direction2Check: direction,
      piecesArray,
    });
    if (movePlan.length > 0) {
      validDirections.push(direction);
    }
  }
  // remove the direction the piece is currently facing. Obviously that's
  // not valid, and you need to turn it to a different direction

  const currentDirIndex = validDirections.findIndex((dir) => {
    if (dir === piece2Rotate.direction) {
      return true;
    } else {
      return false;
    }
  });

  if (currentDirIndex > -1) {
    validDirections.splice(currentDirIndex, 1);
  }

  if (turn === piece2Rotate.player && validDirections.length > 0) {
    return {
      available: true,
      validDirections,
    };
  } else {
    return {
      available: false,
      validDirections: [],
    };
  }
};

const getCoords2Rotate = ({ pieces, pieces2Consider }) => {
  for (const piece of [...pieces2Consider]) {
    const rotateResult = isRilRotateAvailable({
      coords: piece.coords,
      piecesArray: pieces2Consider,
    });
    if (rotateResult.available === true) {
      return {
        coords: piece.coords,
        direction:
          rotateResult.validDirections[
            getRandomIntInclusive({
              min: 0,
              max: rotateResult.validDirections.length - 1,
            })
          ],
      };
    }
  }
};

const isRilRemoveAvailable = ({ coords, pieces2Consider }) => {
  const matchResult = matchService.getCurrentMatch();
  if (matchResult.status != 200) {
    // no current match
    return;
  }
  const piece2Remove = gameRilCoordState({
    coords,
    piecesArray: pieces2Consider,
  });
  if (piece2Remove.player == "Empty") {
    return { available: false };
  }
  const match = matchResult.currentMatch;
  const turnAsPlayer =
    match.turn === match.p1UserID ? "playerOne" : "playerTwo";
  switch (turnAsPlayer) {
    case "playerOne":
      const p1RemovesUsed = match?.p1RemovesUsed ?? 0;
      if (p1RemovesUsed < P1_REMOVES) {
        return { available: true };
      } else {
        return { available: false };
      }
      break;
    case "playerTwo":
      const p2RemovesUsed = match?.p2RemovesUsed ?? 0;
      if (p2RemovesUsed < P2_REMOVES) {
        return { available: true };
      } else {
        return { available: false };
      }
      break;
  }
};

const getCoords2Remove = (pieces2Consider) => {
  const currentMatch = matchService.getCurrentMatch().currentMatch;
  const wartPlayingAs =
    currentMatch.p1UserID === COMPUTER_USERID ? "playerOne" : "playerTwo";
  const removeOptions = [];
  for (const piece of [...pieces2Consider]) {
    if (piece.player === wartPlayingAs) {
      continue; // so he doesn't remove is own pieces
    }
    const removeResult = isRilRemoveAvailable({
      coords: piece.coords,
      pieces2Consider,
    });
    if (removeResult.available === true) {
      const index = coordInPieces({
        coords: piece.coords,
        piecesArray: pieces2Consider,
      });
      const pieces2ConsiderClone = structuredClone(pieces2Consider);
      pieces2ConsiderClone.splice(index);
      const { p1Score, p2Score } = calcScoreDelta(pieces2ConsiderClone);
      const scoreDeltaAbs = Math.abs(p1Score - p2Score);
      removeOptions.push({ piece, p1Score, p2Score, scoreDeltaAbs });
    }
  }
  if (removeOptions.length === 0) {
    return null;
  }
  let bestPiece2Remove = null;
  let maxScoreDelta = 0;
  let foundOne = false;
  for (const option of removeOptions) {
    if (option.scoreDeltaAbs > maxScoreDelta) {
      maxScoreDelta = option.scoreDeltaAbs;
      if (!isCenter(option.piece)) {
        bestPiece2Remove = option.piece;
        foundOne = true;
      }
    }
  }
  if (foundOne) {
    return bestPiece2Remove.coords;
  } else {
    return null;
  }
};

const calcEachPostionAdd = ({ pieces, pieces2Consider }) => {
  const result = [];
  for (const piece of [...pieces2Consider]) {
    // need a temp place to add everything to in their new position
    let tempPieces = [...pieces];
    const pushResult = isRilPushAvailable({
      coords: piece.coords,
      piecesArray: pieces2Consider,
    });
    if (pushResult.available === true && pushResult.movePlan.length > 0) {
      tempPieces = moveRilPieces({ tempPieces, movePlan: pushResult.movePlan });
      tempPieces = addComputerPiece({ piece, tempPieces });
      const { p1Score, p2Score } = calcScoreDelta(tempPieces);
      result.push({
        p1Score: p1Score,
        p2Score: p2Score,
        coords: piece.coords,
        pieces: [...tempPieces],
        deltaP2: p2Score - p1Score,
        deltaP1: p1Score - p2Score,
        sortKey: getRandomIntInclusive({ min: 0, Max: 100 }),
      });
    }
    if (isCenter(piece.coords) === true && piece.player === "Empty") {
      tempPieces = addComputerPiece({ piece, tempPieces });
      const { p1Score, p2Score } = calcScoreDelta(tempPieces);
      result.push({
        p1Score: p1Score,
        p2Score: p2Score,
        coords: piece.coords,
        pieces: [...tempPieces],
        deltaP2: p2Score - p1Score,
        deltaP1: p1Score - p2Score,
        sortKey: getRandomIntInclusive({ min: 0, Max: 100 }),
      });
    }
  } //end pieces2Consider loop

  // another function picks the coord to play on by the largest delta in points
  // and when there are multiple results with the same delta, it picks the last one
  // so let's mix it up:
  return result.sort((a, b) => a.sortKey - b.sortKey);
};

const calcHexScore = ({ piece, piecesArray }) => {
  let hexScore = 0;
  const numCommonBorders = commonBorders({
    coords: piece.coords,
    player: piece.player,
    piecesArray,
  });
  hexScore = SCOREBONACCI[numCommonBorders];
  if (isDouble(piece.coords)) {
    hexScore = hexScore * 2;
  }
  return hexScore;
};

const commonBorders = ({ coords, player, piecesArray }) => {
  var commonBorders = 0;
  for (const direction of DIRECTIONS) {
    const neighbor = getRilNeighbor({ coords, direction, piecesArray });
    if (neighbor.player === player) {
      commonBorders++;
    }
  }
  return commonBorders;
};

const getRilNeighbor = ({ coords, direction, piecesArray }) => {
  const neighorCoords = getNeighborCoords({ coords, direction });
  if (!isOnGrid(neighorCoords)) {
    // this means the neighbor is not on the grid
    return {
      player: "Empty",
      direction: "Empty",
      coords: { s: -99, q: -99, r: -99 },
    };
  }

  const neighborPiece = gameCoordState({ coords: neighorCoords, piecesArray });
  return neighborPiece;
};

const gameCoordState = ({ coords, piecesArray }) => {
  var piece = {
    player: "Empty",
    direction: "Empty",
    coords: coords,
  };
  const index = coordInPieces({ coords, piecesArray });
  if (index != -1) {
    piece.player = piecesArray[index].player;
    piece.direction = piecesArray[index].direction;
  }
  return piece;
};

const bestCoords = (results) => {
  if (results.length === 0) {
    console.log("bestCoords: Empty results, returning default coords");
    return { p1Coords: { s: 0, r: 0, q: 0 }, p2Coords: { s: 0, r: 0, q: 0 }, piecesP1: [], piecesP2: [] };
  }
  let maxCoordsP2 = results[0].coords;
  let maxDeltaP2 = 0;
  let maxCoordsP1 = results[0].coords;
  let maxDeltaP1 = 0;
  let piecesP1 = results[0].pieces;
  let piecesP2 = results[0].pieces;

  for (const result of [...results]) {
    if (result.deltaP2 >= maxDeltaP2) {
      maxDeltaP2 = result.deltaP2;
      maxCoordsP2 = result.coords;
      piecesP2 = result.pieces;
    }
    if (result.deltaP1 >= maxDeltaP1) {
      maxDeltaP1 = result.deltaP1;
      maxCoordsP1 = result.coords;
      piecesP1 = result.pieces;
    }
  }

  return {
    p1Coords: maxCoordsP1,
    piecesP1,
    p2Coords: maxCoordsP2,
    piecesP2,
  };
};

const oppositeDirection = (direction) => {
  switch (direction) {
    case "up":
      return "down";
    case "upRight":
      return "downLeft";
    case "downRight":
      return "upLeft";
    case "down":
      return "up";
    case "downLeft":
      return "upRight";
    case "upLeft":
      return "downRight";
  }
};

const getRandomIntInclusive = ({ min, max }) => {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

// if there is a neighor facing it, then block it
const bestDirection = (coords2Play) => {
  let p1Dir = "";
  let p2Dir = "";
  for (const direction of DIRECTIONS) {
    const neighborP1 = getRilNeighbor({
      direction,
      coords: coords2Play.p1Coords,
      piecesArray: coords2Play.piecesP1,
    });
    if (neighborP1.direction === oppositeDirection(direction)) {
      p1Dir = direction;
    }
    const neighborP2 = getRilNeighbor({
      direction,
      coords: coords2Play.p2Coords,
      piecesArray: coords2Play.piecesP2,
    });
    if (neighborP2.direction === oppositeDirection(direction)) {
      p2Dir = direction;
    }
    // if the neighbor is a double, then overrride previous direction
    // and point to the double
    if (isDouble(neighborP1.coords)) {
      p1Dir = direction;
    }
    if (isDouble(neighborP2.coords)) {
      p2Dir = direction;
    }
  }

  if (p1Dir === "") {
    p1Dir =
      DIRECTIONS[getRandomIntInclusive({ min: 0, max: DIRECTIONS.length - 1 })];
  }

  if (p2Dir === "") {
    p2Dir =
      DIRECTIONS[getRandomIntInclusive({ min: 0, max: DIRECTIONS.length - 1 })];
  }
  return { p1Dir, p2Dir };
};

const bestMove = () => {
  const pieces = matchService.getPieces().pieces;
  console.log("bestMove: pieces is: " + pieces + " (type: " + typeof pieces + "), length: " + (pieces ? pieces.length : 0));
  const pieces2Consider = addCenterHexes([...pieces]); //this is what we check
  const results = calcEachPostionAdd({ pieces, pieces2Consider });
  console.log("bestMove: results length is: " + results.length);
  const currentMatch = matchService.getCurrentMatch().currentMatch;

  if (results.length === 0) {
    console.log("bestMove: No results found, returning fallback move");
    // ususally delete before rotate, but not always
    if (Math.floor(Math.random() * 2) === 1) {
      const coords2Delete = getCoords2Remove(pieces2Consider);
      if (coords2Delete != null) {
        return {
          action: "remove",
          piece: {
            player:
              currentMatch.p1UserID === COMPUTER_USERID
                ? "playerOne"
                : "playerTwo",
            coords: coords2Delete,
            direction: "up",
          },
        };
      }
    }

    // means we need to rotate
    const coords2Rotate = getCoords2Rotate({ pieces, pieces2Consider });
    if (typeof coords2Rotate != "undefined") {
      return {
        action: "rotate",
        piece: {
          player:
            currentMatch.p1UserID === COMPUTER_USERID
              ? "playerOne"
              : "playerTwo",
          coords: coords2Rotate.coords,
          direction: coords2Rotate.direction,
        },
      };
    }
    
    console.log("bestMove: Fallback moves not available, returning empty result");
    return { action: "push", piece: { coords: { s: 0, r: 0, q: 0 }, direction: "up", player: "Empty" } };
  }

  const coords2Play = bestCoords(results);
  const dirs2Play = bestDirection(coords2Play);

  if (currentMatch.p1UserID === COMPUTER_USERID) {
    const piece = {
      coords: coords2Play.p1Coords,
      direction: dirs2Play.p1Dir,
      player: "playerOne",
    };
    return { action: "push", piece };
  }
  if (currentMatch.p2UserID === COMPUTER_USERID) {
    const piece = {
      coords: coords2Play.p2Coords,
      direction: dirs2Play.p2Dir,
      player: "playerTwo",
    };
    return { action: "push", piece };
  }
  console.log("Error: Neither player one nor player two is computer");
}; // end function

export const rilgorithm = {
  bestMove,
};
